import React from 'react'
import * as Sentry from '@sentry/react'
import { isFlightRecorderEnabled, recordFlightEvent } from '../debug/flightRecorder'

/**
 * Known business codes → user-facing Spanish copy.
 * Prefer these over raw API English / JSON payloads.
 */
const errorMessages = {
  SEAT_TAKEN: {
    title: 'Asiento no disponible',
    message:
      'Este asiento ya no está libre. Elige otro en el mapa.',
  },
  INVALID_SEAT: {
    title: 'Asiento no válido',
    message: 'El asiento seleccionado no es válido. Elige otro.',
  },
  MAX_PASSENGERS: {
    title: 'Error',
    message: 'Has excedido el número máximo de pasajeros',
  },
  TIMEOUT: {
    title: 'Error',
    message: 'Tiempo de sesión agotado',
  },
  DEFAULT: {
    title: 'Error',
    message: 'Ocurrió un error al procesar tu solicitud',
  },
}

function resolveKnownError (code) {
  if (!code) return null
  const entry = errorMessages[String(code)]
  if (!entry) return null
  if (typeof entry === 'string') {
    return { title: 'Error', message: entry }
  }
  return { title: entry.title || 'Error', message: entry.message }
}

/**
 * Intenta interpretar `response.data.message` (string u objeto).
 * @param {unknown} raw
 * @returns {{ code: string | null, message: string | null }}
 */
function normalizeApiMessagePayload (raw) {
  if (raw == null) return { code: null, message: null }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const code = raw.code != null ? String(raw.code) : null
    let message = null
    if (raw.message != null) {
      if (typeof raw.message === 'object') {
        // Nested envelope — prefer inner code if present
        const inner = normalizeApiMessagePayload(raw.message)
        return {
          code: code || inner.code,
          message: inner.message,
        }
      }
      message = String(raw.message).trim() || null
    }
    return { code, message }
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return { code: null, message: null }
    // Never surface stringified JSON envelopes as the user message
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (parsed && typeof parsed === 'object') {
          return normalizeApiMessagePayload(parsed)
        }
      } catch {
        return { code: null, message: null }
      }
    }
    return { code: null, message: trimmed }
  }
  return { code: null, message: null }
}

/**
 * Extrae texto útil de un error Axios (o similar) para UI y logs.
 * @param {unknown} error
 * @returns {{ userMessage: string, userTitle: string, code: string | null, debug: object }}
 */
function extractFromAxiosLikeError (error) {
  const res = error && typeof error === 'object' && 'response' in error ? error.response : null
  const data = res && typeof res.data === 'object' && res.data !== null ? res.data : null
  const status = res && res.status != null ? Number(res.status) : null
  const statusText = res && typeof res.statusText === 'string' ? res.statusText.trim() : ''
  const config = error && typeof error === 'object' && 'config' in error ? error.config : null
  const url =
    (config && typeof config.url === 'string' && config.url) ||
    (res && res.config && typeof res.config?.url === 'string' && res.config.url) ||
    ''

  let code = null
  let fromPayload = null

  if (data) {
    if ('message' in data) {
      const n = normalizeApiMessagePayload(data.message)
      code = n.code
      fromPayload = n.message
    }
    // Top-level envelope: { code, message } (no nested message object)
    if (!code && data.code != null) {
      code = String(data.code)
    }
    if (!fromPayload && !code) {
      const n = normalizeApiMessagePayload(data)
      code = n.code
      fromPayload = n.message
    }
  }

  // Some clients put the envelope only on Error.message
  if (!code) {
    const axiosRaw = error instanceof Error ? error.message : ''
    if (axiosRaw && (axiosRaw.trim().startsWith('{') || axiosRaw.includes('SEAT_TAKEN'))) {
      const n = normalizeApiMessagePayload(axiosRaw)
      if (n.code) {
        code = n.code
        fromPayload = fromPayload || n.message
      }
    }
  }

  const axiosMsg = error instanceof Error ? error.message : String(error?.message || error || '')
  const axiosCode = error && typeof error === 'object' && 'code' in error ? String(error.code || '') : ''

  const known = resolveKnownError(code)
  let userMessage = null
  let userTitle = 'Error'

  if (known) {
    userMessage = known.message
    userTitle = known.title
  } else if (fromPayload && !looksLikeRawJson(fromPayload)) {
    userMessage = fromPayload
  } else if (status != null && Number.isFinite(status)) {
    const bit = statusText ? ` ${statusText}` : ''
    userMessage = `Error del servidor (HTTP ${status})${bit}.`.trim()
    if (axiosMsg && !looksLikeRawJson(axiosMsg) && !userMessage.includes(axiosMsg)) {
      userMessage = `${userMessage} ${axiosMsg}`.trim()
    }
  } else if (axiosMsg && !looksLikeRawJson(axiosMsg)) {
    userMessage = axiosMsg
  } else if (axiosCode === 'ECONNABORTED') {
    userMessage = 'La solicitud tardó demasiado (tiempo de espera agotado).'
  } else if (axiosCode === 'ERR_NETWORK' || /network/i.test(axiosMsg)) {
    userMessage = 'No hay conexión con el servidor. Verifica la red e intenta de nuevo.'
  }

  if (!userMessage || !String(userMessage).trim()) {
    userMessage = errorMessages.DEFAULT.message
    userTitle = errorMessages.DEFAULT.title
  }

  return {
    userMessage: String(userMessage).trim(),
    userTitle,
    code,
    debug: {
      status,
      statusText,
      url,
      responseData: data,
      axiosCode: axiosCode || undefined,
      axiosMessage: axiosMsg || undefined,
    },
  }
}

function looksLikeRawJson (text) {
  const t = String(text || '').trim()
  return t.startsWith('{') || t.startsWith('[')
}

/**
 * Procesa errores de API (p. ej. Axios) para mostrar al usuario y registrar en consola.
 * @param {unknown} error
 * @param {Record<string, unknown>} [context]
 * @returns {{ userMessage: string, userTitle: string, errorData: object }}
 */
export const handleApiError = (error, context = {}) => {
  try {
    const extracted = extractFromAxiosLikeError(error)

    const errorData = {
      code: extracted.code,
      userMessage: extracted.userMessage,
      userTitle: extracted.userTitle,
      context,
      timestamp: new Date().toISOString(),
      stack: error instanceof Error ? error.stack : undefined,
      debug: extracted.debug,
    }

    console.error('[handleApiError]', {
      userMessage: extracted.userMessage,
      userTitle: extracted.userTitle,
      status: extracted.debug.status,
      url: extracted.debug.url,
      responseData: extracted.debug.responseData,
      axiosMessage: extracted.debug.axiosMessage,
      context,
    })

    if (isFlightRecorderEnabled()) {
      recordFlightEvent(
        'ERROR:API',
        {
          code: extracted.code,
          status: extracted.debug.status,
          url: extracted.debug.url
            ? String(extracted.debug.url).split('?')[0].slice(0, 200)
            : undefined,
          action: context?.action,
          seatId: context?.seatId,
          userTitle: extracted.userTitle,
        },
        'handleApiError',
      )
    }

    return {
      userMessage: extracted.userMessage,
      userTitle: extracted.userTitle,
      errorData,
    }
  } catch (unexpected) {
    const fallback = errorMessages.DEFAULT
    console.error('[handleApiError] fallo interno al interpretar error', {
      unexpected: unexpected instanceof Error ? unexpected.message : String(unexpected),
      original: error,
      context,
    })
    return {
      userMessage: fallback.message,
      userTitle: fallback.title,
      errorData: {
        code: 'HANDLER_ERROR',
        userMessage: fallback.message,
        userTitle: fallback.title,
        originalError: error,
        unexpected,
        context,
        timestamp: new Date().toISOString(),
      },
    }
  }
}

/**
 * @param {string} message
 * @param {{ setupModal: Function, showModal: Function, hideModal: Function }} modalContext
 * @param {{ title?: string }} [options]
 */
export const showErrorAlert = (message, modalContext, options = {}) => {
  const title = options.title || 'Error'
  if (modalContext && modalContext.setupModal && modalContext.showModal) {
    modalContext.setupModal({
      title,
      content: <p>{message}</p>,
      actions: {
        positive: {
          title: 'Entendido',
          onClick: () => modalContext.hideModal(),
        },
      },
    })
    modalContext.showModal()
  } else {
    alert(`${title}\n\n${message}`)
  }
}

export const reportError = (error, metadata = {}) => {
  if (typeof Sentry !== 'undefined') {
    Sentry.captureException(error, {
      tags: { errorCode: metadata.code },
      extra: metadata,
    })
  }
  console.error('Error reportado:', { error, metadata })
}
